import React, { Fragment } from "react";

class BlogBuilder {
  list = [];

  constructor({ title, image, description }) {
    this.title = title;
    this.image = image;
    this.description = description;
    this.published = null;
    this.readTime = null;
    this.author = null; // author info: { name, logo, designation }
  }

  addParagraph = (props) => {
    this.list.push(
      <p key={this.list.length} className="lead">
        {props}
      </p>
    );
    return this;
  };

  addHeading = (props) => {
    this.list.push(
      <Fragment key={this.list.length}>
        <h1 className="">{props}</h1>
        <hr />
      </Fragment>
    );
    return this;
  };

  addImage = (img) => {
    this.image = img;
    return this;
  };

  setPublished = (dateString) => {
    this.published = dateString;
    return this;
  };

  setReadTime = (minutes) => {
    this.readTime = `${minutes} min read`;
    return this;
  };

  // set author info for the sidebar
  setAuthor = ({ name = "", logo = "", designation = "", company = "" } = {}) => {
    this.author = { name, logo, designation, company };
    return this;
  };

  // add an inline image block inside the article
  // options: { size: 'full' | 'contained' | 'small', align: 'left' | 'right' | 'center' }
  addImageBlock = (imgSrc, alt = "", caption = "", options = {}) => {
    const { size = "contained", align = "center" } = options;
    const sizeClass = `size-${size}`;
    const alignClass = `align-${align}`;

    this.list.push(
      <figure key={this.list.length} className={`blog-inline-image ${sizeClass} ${alignClass}`}>
        <img src={imgSrc} alt={alt} className="img-fluid rounded" />
        {caption && <figcaption className="text-muted">{caption}</figcaption>}
      </figure>
    );
    return this;
  };

  // add a minimalist, responsive table
  // table: { headers: [..], rows: [[..], [..]] }
  addTable = (table) => {
    const { headers = [], rows = [] } = table || {};
    this.list.push(
      <div key={this.list.length} className="blog-table-wrap my-3">
        <table className="blog-table" role="table">
          <thead>
            <tr>
              {headers.map((h, i) => (
                <th key={`h-${i}`} scope="col">
                  {h}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {rows.map((r, ri) => (
              <tr key={`r-${ri}`}>
                {r.map((c, ci) => (
                  <td key={`c-${ri}-${ci}`}>{c}</td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    );
    return this;
  };

  // add a list of bullet points (array of strings)
  addBulletList = (items = []) => {
    this.list.push(
      <ul key={this.list.length} className="blog-bullet-list">
        {items.map((it, i) => (
          <li key={`li-${i}`} dangerouslySetInnerHTML={{ __html: it }} />
        ))}
      </ul>
    );
    return this;
  };

  // subheading helper (level: 2..4)
  addSubHeading = (text = "", level = 2) => {
    const key = `sub-${this.list.length}`;
    if (level === 2) this.list.push(<h2 key={key} className="blog-subheading">{text}</h2>);
    else if (level === 3) this.list.push(<h3 key={key} className="blog-subheading h3">{text}</h3>);
    else this.list.push(<h4 key={key} className="blog-subheading h4">{text}</h4>);
    return this;
  };

  // code block: code string and optional language
  addCodeBlock = (code = "", language = "") => {
    this.list.push(
      <pre key={this.list.length} className="blog-code-block">
        <code className={language ? `language-${language}` : ""}>{code}</code>
      </pre>
    );
    return this;
  };

  // Return only the article content fragment. Page-level layout (sidebar + content) is handled by BlogPost.
  getBlog = () => {
    return <div className="blog-inner-content">{this.list}</div>;
  };
}

export { BlogBuilder };
